/**
 * NotificationContext
 * Provides global notification state and actions throughout the app
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useMemo,
} from 'react'
import { useNotifications } from '@/hooks/useNotifications'
import type { Notification, DismissAllOptions } from '@/types/notifications'

interface NotificationContextValue {
  // Data
  notifications: Notification[]
  unreadCount: number
  totalCount: number
  hasUnreadNotifications: boolean

  // Loading states
  loading: boolean
  isFetching: boolean
  error: Error | null

  // Actions
  markAsRead: (id: number) => Promise<void>
  markAllAsRead: (options?: DismissAllOptions) => Promise<void>
  refetch: () => void

  // Polling control
  isPollingEnabled: boolean
  setPollingEnabled: (enabled: boolean) => void

  // UI state
  isDropdownOpen: boolean
  setDropdownOpen: (open: boolean) => void
}

const NotificationContext = createContext<NotificationContextValue | undefined>(
  undefined
)

interface NotificationProviderProps {
  children: ReactNode
  /**
   * Number of notifications to fetch for the dropdown
   * @default 50
   */
  initialLimit?: number
  /**
   * Enable polling for new notifications
   * @default true
   */
  enablePolling?: boolean
  /**
   * Polling interval in milliseconds
   * @default 120000 (2 minutes)
   */
  pollingInterval?: number
}

export function NotificationProvider({
  children,
  initialLimit = 50,
  enablePolling = true,
  pollingInterval = 2 * 60 * 1000,
}: NotificationProviderProps) {
  // UI state for dropdown
  const [isDropdownOpen, setDropdownOpen] = useState(false)

  // Use the notifications hook
  const {
    notifications,
    unreadCount,
    totalCount,
    hasUnreadNotifications,
    isLoading,
    isFetching,
    error,
    markAsRead: hookMarkAsRead,
    markAllAsRead: hookMarkAllAsRead,
    refetch,
    isPollingEnabled,
    setPollingEnabled,
  } = useNotifications({
    limit: initialLimit,
    enablePolling,
    pollingInterval,
  })

  // Wrap markAsRead to potentially close dropdown
  const markAsRead = useCallback(
    async (id: number) => {
      await hookMarkAsRead(id)
    },
    [hookMarkAsRead]
  )

  // Wrap markAllAsRead
  const markAllAsRead = useCallback(
    async (options?: DismissAllOptions) => {
      await hookMarkAllAsRead(options)
    },
    [hookMarkAllAsRead]
  )

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<NotificationContextValue>(
    () => ({
      notifications,
      unreadCount,
      totalCount,
      hasUnreadNotifications,
      loading: isLoading,
      isFetching,
      error,
      markAsRead,
      markAllAsRead,
      refetch,
      isPollingEnabled,
      setPollingEnabled,
      isDropdownOpen,
      setDropdownOpen,
    }),
    [
      notifications,
      unreadCount,
      totalCount,
      hasUnreadNotifications,
      isLoading,
      isFetching,
      error,
      markAsRead,
      markAllAsRead,
      refetch,
      isPollingEnabled,
      setPollingEnabled,
      isDropdownOpen,
    ]
  )

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

/**
 * Hook to access notification context
 * @throws Error if used outside of NotificationProvider
 */
export function useNotificationContext(): NotificationContextValue {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error(
      'useNotificationContext must be used within a NotificationProvider'
    )
  }
  return context
}

/**
 * Optional hook that doesn't throw if context is unavailable
 * Useful for components that may or may not be within the provider
 */
export function useOptionalNotificationContext(): NotificationContextValue | null {
  const context = useContext(NotificationContext)
  return context ?? null
}
